<script setup lang="ts">
import type { FeedActivity } from '@/types/customer/ActivitiesFeed'
import ActivityCardBase from './ActivityCardBase.vue'

const { t, locale } = useI18n()

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const getLocalizedText = (
  value: FeedActivity['title'] | FeedActivity['description'] | string | null | undefined
): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    return value[locale.value as keyof typeof value] || value.en || ''
  }
  return ''
}

const isExecution = computed(() => props.activity.type === 'marketing.flow.execution')

const description = computed(() => {
  const descText = getLocalizedText(props.activity.description)
  if (descText) return descText

  const flowName = props.activity.data?.marketing_flow_name || props.activity.data?.name || ''

  if (isExecution.value) {
    return t('pages.customer.customerDetailPage.blocks.activity.marketingFlowExecution.text', {
      name: flowName
    })
  }

  const points = props.activity.data?.points || 0
  return t('pages.customer.customerDetailPage.blocks.activity.marketingFlow.text', {
    points,
    name: flowName
  })
})

const title = computed(() => {
  return getLocalizedText(props.activity.title) || ''
})

const iconColor = computed(() => {
  return 'bg-yellow-400'
})
</script>

<template>
  <ActivityCardBase
    :icon="activity.icon || 'HeroBoltIconSolid'"
    :icon-color="iconColor"
    :occurred-at="activity.occurred_at"
    :show-connector="showConnector"
  >
    <template #title>
      <p class="text-sm font-medium text-foreground">{{ title }}</p>
    </template>
    <template #description>
      <p class="mt-1 text-sm text-muted-foreground">{{ description }}</p>
    </template>
  </ActivityCardBase>
</template>
